import React, { ReactNode, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Divider, Form, Modal, Row } from 'antd';
import {
    BankOutlined,
    FileProtectOutlined,
    FileTextOutlined,
    LockOutlined,
    MailOutlined,
    PhoneOutlined,
    SettingOutlined,
    StopOutlined,
    TeamOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { ApiKeys } from './ApiKeys';
import { FieldData } from 'rc-field-form/es/interface';
import {
    IAntDFormChangeFieldName,
    IFieldLayout,
    IPOSTUser2FAEnableResponses,
    IShowForm,
    NotificationType,
} from '../../../interfaces';
import { usePagesStoreCurrentPage, useProfile, useProfileIsRight, useUser } from '../../../hooks';
import {
    addNotification,
    apiMessagesDeleteMessageByFieldName,
    getUserById,
    pathUser2FADisable,
    postUser2FAEnableStep1,
    postUser2FAEnableStep2,
} from '../../../middleware';
import { dateText2Utc, fullName, generateError } from '../../../instruments';
import { ITrPage, tB, tI, tP } from '../../../translate';
import {
    CardInfoToolBar,
    CardMainLayout,
    Catcher,
    FieldInfoAdvanced,
    LanguageSelector,
    LinkComponent,
    cardLayoutOptions,
} from '../../../Components';
import { Form2FAPassword, Form2FAQRCode, ShowUserFeatureFlags } from '../Components';
import Styles from '../../styles/m_itemCardStyles.less';
import ViewStyles from '../../styles/m_viewStyles.less';

enum ModalType2FA {
    UNKNOWN = 'unknown',
    ENABLE_1 = 'enable1',
    ENABLE_2 = 'enable2',
    DISABLE = 'disable',
}

export const ShowUser: React.FC<IShowForm> = observer(({ setEditMode }: IShowForm) => {
    const layout: IFieldLayout = {
        labelCol: {
            flex: '0 1 180px',
        },
        valCol: {
            flex: '1 1 270px',
        },
    };
    const {
        id,
        company,
        companyId,
        docsAcceptedAt,
        email,
        failedLoginCount,
        firstName = '',
        func,
        lastName = '',
        mfaEnabledAt,
        notes,
        phoneNumber,
        suspended,
        userType,
    } = useUser();
    const { id: myId, companyId: myCompanyId } = useProfile();
    const isMe = myId === id;
    const isMyCompany = myCompanyId === companyId;
    const isAdmin = useProfileIsRight('ADMIN');
    const isLord = useProfileIsRight('LORD');
    const isRoot = useProfileIsRight('ROOT');
    const userName = `${firstName} ${lastName}`;
    const { title } = usePagesStoreCurrentPage();
    const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
    const [modalType, setModalType] = useState<ModalType2FA>(ModalType2FA.UNKNOWN);
    const [passwordModal, setPasswordModal] = useState<string>('');
    const [enableFormField, setEnableFormField] = useState<IPOSTUser2FAEnableResponses>();

    const [form] = Form.useForm();
    const [initialValues] = useState({ secretWord: '' });

    const showTitle = isMe
        ? tP(title as ITrPage)
        : firstName || lastName
        ? userName
        : `${tP(title as ITrPage)} ${id ? '#' + id : ''}`;

    useEffect(() => {
        if (document.title !== showTitle) {
            document.title = showTitle;
        }
    }, [showTitle]);

    useEffect(() => {
        if (mfaEnabledAt) {
            if (isMe || isRoot || isLord || isMyCompany) {
                setModalType(ModalType2FA.DISABLE);
            }
        } else {
            if (isMe) {
                setModalType(ModalType2FA.ENABLE_1);
            }
        }
        // ToDo: 27.05.2025 - to DEBUG !!!!!!!!!!!!!!!!!!!!
        // setModalType(ModalType2FA.ENABLE_2);
    }, [mfaEnabledAt, isMe, isLord, isRoot]);

    const setOpenModal = () => {
        setIsOpenModal(true);
    };
    const setCloseModal = () => {
        setIsOpenModal(false);
        setPasswordModal('');
    };

    const setSubmit = async (modalType: ModalType2FA) => {
        switch (modalType) {
            case ModalType2FA.DISABLE:
                const isOkDisable = await pathUser2FADisable({ userId: id, password: passwordModal });
                if (isOkDisable) {
                    setCloseModal();
                    setPasswordModal('');
                    form.setFieldsValue(initialValues);
                    form.resetFields();

                    await getUserById(id);
                    // ToDo: 26.05.2025 - add translate
                    addNotification({
                        type: NotificationType.INFO,
                        message: '2-Step Authentication',
                        description: `${fullName(firstName, lastName)} successfully disabled 2-Step Verification`,
                        isTranslated: true,
                        duration: 20,
                    });
                } else {
                    form.validateFields();
                }

                break;
            case ModalType2FA.ENABLE_1:
                const resp = await postUser2FAEnableStep1({ password: passwordModal });
                if (resp) {
                    setEnableFormField(resp);
                    setModalType(ModalType2FA.ENABLE_2);
                    setPasswordModal('');
                    form.setFieldsValue(initialValues);
                    form.resetFields();
                } else {
                    form.validateFields();
                }
                break;
            case ModalType2FA.ENABLE_2:
                const isOkEnable = await postUser2FAEnableStep2({ totp: passwordModal });
                if (isOkEnable) {
                    await getUserById(id);
                    setModalType(ModalType2FA.DISABLE);
                    setCloseModal();
                    form.setFieldsValue(initialValues);
                    form.resetFields();
                    // ToDo: 26.05.2025 - add translate
                    addNotification({
                        type: NotificationType.INFO,
                        message: '2-Step Authentication',
                        description: `${fullName(firstName, lastName)} successfully enabled 2-Step Authentication`,
                        isTranslated: true,
                        duration: 20,
                    });
                } else {
                    form.validateFields();
                }
                break;
            case ModalType2FA.UNKNOWN:
                break;
            default:
                generateError(modalType);
                console.error(`Not found modalType - "${modalType}"`);
                break;
        }
    };
    const onFieldsChange = (fields: FieldData[]): void => {
        const password = fields.find((f: FieldData) => (f.name as Array<string>)[0] === 'secretWord')?.value;
        if (password !== passwordModal) {
            const fieldNameChange = fields && fields[0] && (fields[0].name as IAntDFormChangeFieldName);
            if (fieldNameChange) {
                apiMessagesDeleteMessageByFieldName(fieldNameChange);
            }
        }
        setPasswordModal(password);
    };
    let modalTitle = 'XXX';
    let modalBody: ReactNode = null;
    let modalSubmit = false;

    // ToDo: 26.05.2025 - add translate
    switch (modalType) {
        case ModalType2FA.DISABLE:
            modalTitle = 'Disable 2-Step Authentication';
            modalBody = <Form2FAPassword disableLabelUser={isMe ? 'you' : fullName(firstName, lastName) || ''} />;
            modalSubmit = Boolean(passwordModal?.length);
            break;
        case ModalType2FA.ENABLE_1:
            modalTitle = 'Enable 2-Step Authentication';
            modalBody = <Form2FAPassword />;
            modalSubmit = Boolean(passwordModal?.length);
            break;
        case ModalType2FA.ENABLE_2:
            modalTitle = 'Enable 2-Step Authentication';
            modalBody = <Form2FAQRCode provisioningUri={enableFormField?.provisioningUri} />;
            modalSubmit = Boolean(passwordModal?.length);
            break;
        case ModalType2FA.UNKNOWN:
            modalTitle = '';
            break;
        default:
            generateError(modalType);
            console.error(`Not found modalType - "${modalType}"`);
            break;
    }

    return (
        <Catcher>
            <Row>
                <CardMainLayout
                    columnOption={cardLayoutOptions.cardInfoEdit}
                    idSpinners={['GET_USER']}
                    name={fullName(firstName, lastName)}
                    icon={<UserOutlined />}
                >
                    <FieldInfoAdvanced
                        layout={layout}
                        icon={<SettingOutlined className={Styles.labelIcon} />}
                        title={'Function'}
                    >
                        {func}
                    </FieldInfoAdvanced>
                    <FieldInfoAdvanced
                        layout={layout}
                        icon={<BankOutlined className={Styles.labelIcon} />}
                        title={'Company'}
                    >
                        <LinkComponent currentListComponent={'CompaniesList'} id={companyId} name={company} />
                    </FieldInfoAdvanced>
                    <FieldInfoAdvanced
                        layout={layout}
                        icon={<MailOutlined className={Styles.labelIcon} />}
                        title={'Email'}
                    >
                        {email}
                    </FieldInfoAdvanced>
                    <FieldInfoAdvanced
                        layout={layout}
                        icon={<PhoneOutlined className={Styles.labelIcon} />}
                        title={'Phone number'}
                    >
                        {phoneNumber}
                    </FieldInfoAdvanced>
                    <Divider />
                    <FieldInfoAdvanced
                        layout={layout}
                        icon={<TeamOutlined className={Styles.labelIcon} />}
                        title={'User Type'}
                    >
                        {userType}
                    </FieldInfoAdvanced>
                    <FieldInfoAdvanced
                        layout={layout}
                        icon={<LockOutlined className={Styles.labelIcon} />}
                        title={'Failed login'}
                    >
                        {failedLoginCount}
                    </FieldInfoAdvanced>
                    <FieldInfoAdvanced
                        layout={layout}
                        icon={<StopOutlined className={Styles.labelIcon} />}
                        title={'Suspended'}
                    >
                        {suspended}
                    </FieldInfoAdvanced>
                    <FieldInfoAdvanced
                        layout={layout}
                        icon={<FileTextOutlined className={Styles.labelIcon} />}
                        title={'Notes'}
                    >
                        {notes}
                    </FieldInfoAdvanced>
                    <FieldInfoAdvanced
                        layout={layout}
                        icon={<FileProtectOutlined className={Styles.labelIcon} />}
                        title={'T&Cs and Privacy Policy accepted'}
                    >
                        {docsAcceptedAt ? dateText2Utc(docsAcceptedAt) : tI('Never')}
                    </FieldInfoAdvanced>
                    {isMe ? (
                        <>
                            <Divider />
                            <FieldInfoAdvanced
                                layout={layout}
                                icon={<FileTextOutlined className={Styles.labelIcon} />}
                                title={'Language'}
                            >
                                <LanguageSelector />
                            </FieldInfoAdvanced>
                            <Divider />
                        </>
                    ) : null}
                    <CardInfoToolBar setEditMode={setEditMode}>
                        {modalType === ModalType2FA.UNKNOWN ? null : (
                            <Button danger onClick={setOpenModal}>
                                {modalTitle}
                            </Button>
                        )}
                    </CardInfoToolBar>
                </CardMainLayout>
                {isAdmin && isMe ? <ApiKeys /> : null}
                {isRoot && isMe ? <ShowUserFeatureFlags /> : null}
            </Row>
            {modalType === ModalType2FA.UNKNOWN ? null : (
                <Modal
                    title={modalTitle}
                    open={isOpenModal}
                    onCancel={setCloseModal}
                    wrapClassName={'modal'}
                    footer={[
                        <Button key={'Cancel'} onClick={setCloseModal}>
                            {tB('Cancel')}
                        </Button>,

                        <Button
                            key={'Submit'}
                            type={'primary'}
                            onClick={() => setSubmit(modalType)}
                            disabled={!modalSubmit}
                        >
                            {tB('Submit')}
                        </Button>,
                    ]}
                >
                    <div className={ViewStyles.modalContent2FA}>
                        <Form
                            form={form}
                            name={'Form2FA'}
                            className={ViewStyles.form2FA}
                            initialValues={initialValues}
                            onFieldsChange={onFieldsChange}
                            layout={'horizontal'}
                        >
                            {modalBody}
                        </Form>
                    </div>
                </Modal>
            )}
        </Catcher>
    );
});
